import React from 'react';
import classnames from 'classnames';
import { Trans } from '@lingui/macro';
import { Caption } from '@qover/typography';
import { useMedia } from '@qover/hooks';
import type { ZendeskChat } from '@qover/contexts';
import PhoneIcon from './icons/PhoneIcon';
import ChatIcon from './icons/ChatIcon';
import MailIcon from './icons/MailIcon';
import { SupportCenterHeader } from './SupportCenterHeader';
import ArrowRight from './icons/ArrowRight';
import Spinner from './icons/Spinner';

import {
  SupportCenter,
  ContactAction,
  ContactMethod,
  IconContainer,
  Row,
  ContactLabel,
  MainInfos,
} from './SupportCenter.styles';

type SupportCenterModalProps = {
  zendesk: ZendeskChat;
  showChat?: () => void;
  phone: string;
  email: string;
};

type IconProps = {
  animate?: boolean;
};

type Contact = {
  action?: () => void;
  dataTestButton: string;
  dataTestText: string;
  href?: string;
  icon: (props: IconProps) => JSX.Element;
  name: string;
  title: React.ReactNode;
  value: string | JSX.Element;
};

const getBrokerInfos = (
  zendesk: ZendeskChat,
  phone: string,
  email: string,
  showChat?: () => void,
) => {
  const contactSegments: Contact[] = [
    {
      name: 'mail',
      icon: MailIcon,
      dataTestButton: 'topBar.supportCenterButton.emailus',
      dataTestText: 'supportCenterButton.emailus.text',
      title: <Trans id="general.supportCenter.emailUs" defaults="Email us" />,
      value: email,
      href: `mailto:${email}`,
    },
    {
      name: 'phone',
      // @ts-ignore
      icon: PhoneIcon,
      dataTestButton: 'topBar.supportCenterButton.phoneus',
      dataTestText: 'supportCenterButton.phoneus.text',
      title: <Trans id="general.supportCenter.phoneUs" defaults="Phone us" />,
      value: (
        <div>
          <div>{phone}</div>
          <Trans
            id="general.supportCenter.helpCenterAvailability"
            defaults="Monday-Friday from 9am to 5pm"
          />
        </div>
      ),
      href: `tel:${phone}`,
    },
  ];

  if (showChat) {
    contactSegments.push({
      name: 'chat',
      icon: ChatIcon,
      dataTestButton: 'topBar.supportCenterButton.chatus',
      dataTestText: 'supportCenterButton.chatus.text',
      title: (
        <Trans id="general.supportCenter.chatWithUs" defaults="Chat with us" />
      ),
      value: (
        <Trans
          id="general.supportCenter.helpCenterAvailability"
          defaults="Monday-Friday from 9am to 5pm"
        />
      ),
      action: () =>
        // @ts-ignore
        !window.$zopim ? zendesk.setInitializeZendesk(true) : showChat(),
    });
  }

  return contactSegments;
};

export function SupportCenterModal(
  props: SupportCenterModalProps,
): JSX.Element {
  const { zendesk, showChat, phone, email } = props;
  const isMobile = useMedia(['(max-width: 767px)'], ['modalMobile'], null);

  return (
    <div className={classnames('modal', isMobile)}>
      <SupportCenter>
        <SupportCenterHeader />
        <MainInfos>
          {getBrokerInfos(zendesk, phone, email, showChat).map(
            (
              {
                icon: Icon,
                title,
                action,
                href,
                name,
                value,
                dataTestButton,
                dataTestText,
              },
              idx,
            ) => (
              <Row
                key={idx}
                onClick={action}
                data-test={dataTestButton}
                href={href}
              >
                <IconContainer>
                  <Icon />
                </IconContainer>
                <ContactMethod>
                  <ContactLabel>{title}</ContactLabel>
                  <Caption data-test={dataTestText}>{value}</Caption>
                </ContactMethod>
                <ContactAction>
                  {zendesk.zendeskLoading && name === 'chat' ? (
                    <Spinner />
                  ) : (
                    <ArrowRight />
                  )}
                </ContactAction>
              </Row>
            ),
          )}
        </MainInfos>
      </SupportCenter>
    </div>
  );
}
